import type { QueryScreenState } from 'src/utils/requestError';
import type { CatalogVertical, CatalogFilterValues } from 'src/utils/catalogUrl';

import { useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';

import { isOnline } from 'src/utils/online';
import { hasActiveFilter, queryScreenState } from 'src/utils/requestError';
import { buildCatalogPath, parseCatalogSearch } from 'src/utils/catalogUrl';

import { useCatalogMake, useCatalogModel } from 'src/queries';

/** Everything a catalog link can change; what is left out stays as the address has it. */
export interface CatalogTarget {
  makeSlug?: string;
  modelSlug?: string;
  filters?: CatalogFilterValues;
  page?: number;
}

interface CatalogRouteOptions {
  /**
   * The dealer's page (`/dealer/:sellerId`): its path is the dealer's, so the make and the model are
   * read from and written to the query — `?make=…&model=…` — next to `vertical=rent`.
   */
  dealerId?: string;
}

/**
 * A catalog screen's view of its own address (storefront-catalog spec §4.1): which make and model
 * the path names, which facets and which page the query asks for, and how to get to any other view.
 *
 * Make and model arrive as slugs and are resolved by the catalogue itself — `GET /common/makes?slug=`
 * and `GET /common/models?makeId=&slug=`, one entry or none (`useCatalogMake` / `useCatalogModel`).
 * A slug the catalogue does not know is not an error and not a guess: `unknownSlug` says so, and the
 * screen shows "No cars match these filters" without asking for the feed.
 *
 * `ready` is false only while a slug in the address waits for its lookup; an address with no make
 * is ready at once.
 */
export const useCatalogRoute = (vertical: CatalogVertical, options: CatalogRouteOptions = {}) => {
  const { dealerId } = options;
  const facetsInQuery = dealerId !== undefined;
  const params = useParams<{ make?: string; model?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const makeSlug = (facetsInQuery ? searchParams.get('make') : params.make) || undefined;
  // A model means nothing without its make.
  const modelSlug = makeSlug ? (facetsInQuery ? searchParams.get('model') : params.model) || undefined : undefined;
  const { filters, page } = useMemo(() => parseCatalogSearch(searchParams, vertical), [searchParams, vertical]);

  // A lookup holds the entry, `null` once the catalogue has said it has none, and nothing until then.
  const makeQuery = useCatalogMake(makeSlug);
  const makeAnswer = makeSlug ? makeQuery.data : undefined;
  const make = makeAnswer ?? undefined;
  // Asked only once the make is known: a model's slug is unique within its make, not on its own.
  const modelQuery = useCatalogModel(make?.id, modelSlug);
  const modelAnswer = modelSlug && make ? modelQuery.data : undefined;
  const model = modelAnswer ?? undefined;

  const unknownSlug = makeAnswer === null || modelAnswer === null;
  const ready = unknownSlug || ((!makeSlug || !!make) && (!modelSlug || !!model));
  // The person narrowed the catalog — by the address, the only state it has: an empty answer then
  // means "nothing matched", not "no cars yet".
  const filtered = unknownSlug || !!makeSlug || hasActiveFilter(filters);

  // The one lookup this address still waits for, if any: its failure is the screen's failure. Told
  // by the answer that is missing, so "Try again" asks for the entry the screen actually lacks.
  const pending =
    makeSlug && makeAnswer === undefined ? makeQuery : modelSlug && make && modelAnswer === undefined ? modelQuery : null;
  const lookupState: QueryScreenState = pending
    ? queryScreenState({
        isLoading: pending.isLoading,
        isFetching: pending.isFetching,
        isPaused: pending.isPaused,
        isError: pending.isError,
        error: pending.error,
        hasData: false,
        online: isOnline(),
      })
    : 'ready';
  const retryLookup = useCallback(() => {
    if (pending) pending.refetch();
  }, [pending]);

  /**
   * The address of another view of this catalog. A new make, model or set of facets starts again
   * from the first page, and a new make drops the model; a page on its own keeps everything else.
   */
  const pathTo = useCallback(
    (next: CatalogTarget = {}): string => {
      const makeChanges = 'makeSlug' in next && next.makeSlug !== makeSlug;
      const facetsChange = 'makeSlug' in next || 'modelSlug' in next || 'filters' in next;

      return buildCatalogPath({
        vertical,
        dealerId,
        makeSlug: 'makeSlug' in next ? next.makeSlug : makeSlug,
        modelSlug: 'modelSlug' in next ? next.modelSlug : makeChanges ? undefined : modelSlug,
        filters: 'filters' in next ? next.filters : filters,
        page: 'page' in next ? next.page : facetsChange ? 1 : page,
      });
    },
    [vertical, dealerId, makeSlug, modelSlug, filters, page]
  );

  /** Goes to another view of this catalog: a new history entry, so Back returns to this one. */
  const navigateTo = useCallback(
    (next: CatalogTarget = {}, { replace = false }: { replace?: boolean } = {}) => {
      navigate(pathTo(next), { replace });
    },
    [navigate, pathTo]
  );

  return {
    vertical,
    makeSlug,
    modelSlug,
    /** The make and the model the address names, as the catalogue has them; `undefined` until resolved, and for a slug it does not know. */
    make,
    model,
    makeId: make?.id,
    modelId: model?.id,
    filters,
    page,
    unknownSlug,
    filtered,
    ready,
    lookupState,
    retryLookup,
    pathTo,
    navigateTo,
  };
};

export type CatalogRoute = ReturnType<typeof useCatalogRoute>;
